import * as React from 'react';
import styled, { css } from 'styled-components';

import { caption, colors, spacing, Label } from 'ui/theme';
import { pxToRem } from 'utils/convert';

const StyledFormField = styled.div<{ isHorizontalLayout: boolean }>`
  display: ${(props) => (props.isHorizontalLayout ? 'flex' : 'block')};
  & + & {
    margin-top: ${spacing.plusHalf};
  }
`;

export const HorizontalInput = styled.div`
  flex: 1 1 auto;
`;

export const FormFieldHeader = styled.div<{ isHorizontalLayout: boolean }>`
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacing.half};
  ${({ isHorizontalLayout }) =>
    isHorizontalLayout &&
    css`
      margin-right: ${spacing.x3};
      display: block;
      min-width: ${pxToRem(140)};
      max-width: ${pxToRem(140)};
    `}
`;

export const FormLabel = styled(Label)`
  display: flex;
  align-items: bottom;
  justify-content: flex-start;
  &:after {
    align-self: flex-start;
  }
`;

const Restriction = styled.div`
  ${caption}
  color: ${colors.textSecondary};
`;

const Description = styled.p`
  ${caption}
  color: ${colors.textSecondary};
  margin: -${spacing.half} 0 ${spacing.half} 0;
`;

export const HelpText = styled.div`
  ${caption}
  color: ${colors.textSecondary};
  margin: 7.5px 0 0 0;
  padding: 0;
  white-space: normal;
`;

export const ErrorText = styled(HelpText)`
  color: ${colors.red100};
  margin: 7.5px 0 0 0;
`;

export const WarningText = styled(HelpText)`
  color: ${colors.warning100};
  margin: 7.5px 0 0 0;
`;

export type FormFieldProps = {
  /** additional content to render (the input) */
  children: React.ReactNode;
  /** additional description appearing below the label, and above the input, not used in horizontal layout */
  descriptionText?: string | React.ReactNode;
  /** Text to display as a red error message when the input is in an error state */
  errorText?: string | React.ReactNode;
  /** toggle whether or not the error text is displayed */
  hasError?: boolean;
  /** Text to display as a orange warning message when the input is in an warning state */
  warningText?: string | React.ReactNode;
  /** toggle whether or not the warning text is displayed */
  hasWarning?: boolean;
  /** additional context to display below the input to guide a user */
  helpText?: string | React.ReactNode;
  /** The id of the input that the form field is associated with, clicking a <label> will pass focus to the linked input */
  inputId?: string;
  /** whether to display form field label and field horizontally */
  isHorizontalLayout?: boolean;
  /** whether the form field is disabled */
  isDisabled?: boolean;
  /** show the required asterisk */
  isRequired?: boolean;
  /** The label to display for the form field, leave blank for checkbox implementations */
  labelText?: string | React.ReactNode;
  /** additional text */
  restrictionText?: string | React.ReactNode;
};

/**
  conditionally add a wrapping div for horizontal layouts. This wrapping div
  may break some vertical layouts.

  @see https://github.com/Iterable/iterable-frontend/pull/4708/files#r797961555
*/
const MarkupWrapper: React.FC<React.PropsWithChildren<{ shouldWrapInDiv: boolean }>> = ({
  children,
  shouldWrapInDiv,
}) => (shouldWrapInDiv ? <HorizontalInput>{children}</HorizontalInput> : <>{children}</>);

export const FormField = React.forwardRef(
  (
    {
      children,
      descriptionText = '',
      errorText = '',
      hasError = false,
      warningText = '',
      hasWarning = false,
      helpText = '',
      inputId = '',
      isDisabled = false,
      isHorizontalLayout = false,
      isRequired = false,
      labelText = '',
      restrictionText = '',
      ...props
    }: FormFieldProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const showHeader = labelText || restrictionText;
    return (
      <StyledFormField
        ref={ref}
        data-test="form-field"
        isHorizontalLayout={isHorizontalLayout}
        {...props}
      >
        {showHeader && (
          <FormFieldHeader data-test="form-field-header" isHorizontalLayout={isHorizontalLayout}>
            {labelText && (
              <FormLabel isRequired={isRequired} htmlFor={inputId} isMuted={isDisabled}>
                {labelText}
              </FormLabel>
            )}
            {restrictionText && <Restriction>{restrictionText}</Restriction>}
          </FormFieldHeader>
        )}
        <MarkupWrapper shouldWrapInDiv={isHorizontalLayout}>
          {descriptionText && <Description>{descriptionText}</Description>}
          {React.Children.map(children, (child) => {
            // https://stackoverflow.com/questions/42261783/how-to-assign-the-correct-typing-to-react-cloneelement-when-giving-properties-to
            if (React.isValidElement(child)) {
              // `key` and `ref` from the parent will be preserved
              return React.cloneElement(child, {
                hasError,
                isRequired,
              } as FormFieldProps);
            }

            return null;
          })}
          {helpText && (
            <HelpText data-test="form-field-description" id={`${inputId}-description`}>
              {helpText}
            </HelpText>
          )}
          {hasError && (
            <ErrorText data-test="form-field-error-text" id={`${inputId}-error`}>
              {errorText}
            </ErrorText>
          )}
          {hasWarning && (
            <WarningText data-test="form-field-warning-text" id={`${inputId}-warning`}>
              {warningText}
            </WarningText>
          )}
        </MarkupWrapper>
      </StyledFormField>
    );
  },
);
